'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import type { ProfileMode } from '@/constants/common';
import { COOKIES, PROFILE_MODES } from '@/constants/common';
import { useGetMinProfile } from '@/hooks/useGetMinProfile';
import { CookiesUtils } from '@/utils/helpers/cookies';
import { ProfileFormatter } from '@/utils/helpers/profile';

import {
  ProfileModeContextValue,
  ProfileModeProviderProps
} from './ProfileModeProvider.types';
import {
  getAvailableModes,
  getDefaultMode,
  getStoredProfileMode
} from './ProfileModeProvider.utils';

// ============================================================================
// Context
// ============================================================================

const DEFAULT_CONTEXT: ProfileModeContextValue = {
  profile: undefined,
  profileMode: null,
  switchProfileMode: () => {},
  setProfileMode: () => {},
  isLoading: true,
  availableModes: [],
  canSwitchProfileMode: false
};

const ProfileModeContext = createContext<ProfileModeContextValue | undefined>(
  undefined
);

// ============================================================================
// Hook
// ============================================================================

export const useProfileMode = (): ProfileModeContextValue =>
  useContext(ProfileModeContext) ?? DEFAULT_CONTEXT;

// ============================================================================
// Provider Implementation
// ============================================================================

const ProfileModeProviderInner = ({ children }: ProfileModeProviderProps) => {
  const { profile, loading: isProfileLoading } = useGetMinProfile();

  // Initialize to null to avoid hydration mismatch (cookies can't be read on server)
  const [profileMode, setProfileModeState] = useState<ProfileMode | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Skip validation on next useEffect run (after manual mode change)
  const skipNextValidationRef = useRef(false);

  const setProfileMode = useCallback(
    (mode: ProfileMode, forceReload: boolean = false) => {
      skipNextValidationRef.current = true;
      CookiesUtils.set(COOKIES.PROFILE_MODE, mode);
      setProfileModeState(mode);
      if (forceReload) {
        window.location.reload();
      }
    },
    []
  );

  const switchProfileMode = () => {
    const newProfileMode =
      profileMode === PROFILE_MODES.COMPANY
        ? PROFILE_MODES.CANDIDATE
        : PROFILE_MODES.COMPANY;
    setProfileMode(newProfileMode);
  };

  // Read stored profile mode after mount to avoid hydration mismatch
  useEffect(() => {
    const storedMode = getStoredProfileMode();
    if (storedMode) {
      setProfileModeState(storedMode);
    }
    setIsInitialized(true);
  }, []);

  useEffect(() => {
    if (!profile || !isInitialized) return;

    // Skip validation if mode was just explicitly set by user action
    // (e.g., after creating resume/vacancy). The profile data might be stale,
    // but we trust the explicit user intent.
    if (skipNextValidationRef.current) {
      skipNextValidationRef.current = false;
      return;
    }

    const storedMode = getStoredProfileMode();
    const defaultMode = getDefaultMode(profile);
    const isProfileBoth = ProfileFormatter.isProfileBoth(profile);

    let targetMode: ProfileMode | null;

    if (!storedMode) {
      // No cookies - use default mode
      targetMode = defaultMode;
    } else if (isProfileBoth) {
      // Profile supports both modes - use stored mode
      targetMode = storedMode;
    } else {
      // Profile supports only one mode - force it
      targetMode = defaultMode;
    }

    // Mode is already set correctly - do nothing
    if (!targetMode || targetMode === profileMode) {
      return;
    }

    setProfileMode(targetMode);
  }, [profile, profileMode, setProfileMode, isInitialized]);

  const availableModes = useMemo(() => getAvailableModes(profile), [profile]);

  const isLoading =
    !isInitialized || (isProfileLoading && profileMode === null);
  const canSwitchProfileMode = availableModes.length > 1;

  const value = useMemo<ProfileModeContextValue>(
    () => ({
      profile,
      profileMode,
      switchProfileMode,
      setProfileMode,
      isLoading,
      availableModes,
      canSwitchProfileMode
    }),
    [
      profile,
      profileMode,
      switchProfileMode,
      setProfileMode,
      isLoading,
      availableModes,
      canSwitchProfileMode
    ]
  );

  return (
    <ProfileModeContext.Provider value={value}>
      {children}
    </ProfileModeContext.Provider>
  );
};

// ============================================================================
// Exported Provider (prevents nesting)
// ============================================================================

export const ProfileModeProvider = ({ children }: ProfileModeProviderProps) => {
  const existingContext = useContext(ProfileModeContext);

  if (existingContext) return <>{children}</>;

  return <ProfileModeProviderInner>{children}</ProfileModeProviderInner>;
};
